
import React, { useState, useEffect, useRef } from "react";
import Sidebar from "./Sidebar";
import { useSidebar } from '../hooks';
import { API_BASE_URL } from "../config";
import './css/Settings.css';
import { Session } from "./utils/session";

const authHeaders = () => {
  const token = Session.getToken();

  return {
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`
  };
};
function Settings() {
  const [snapOn, setSnapOn] = useState(false);
  const [statusMsg, setStatusMsg] = useState("");
  const [statusType, setStatusType] = useState("");
  const [settingId, setSettingId] = useState(null);
  const [snapsPerHour, setSnapsPerHour] = useState("");
  const [unsaved, setUnsaved] = useState(false);
  const [saving, setSaving] = useState(false);

  const { toggleSidebar } = useSidebar();

  function parseBool(val) {
    if (val === null || val === undefined) return false;
    if (typeof val === "boolean") return val;
    if (typeof val === "number") return val !== 0;
    if (typeof val === "string") {
      const s = val.trim().toLowerCase();
      const truthy = ["enable", "enabled", "on", "true", "1", "yes"];
      const falsy = ["not", "disable", "disabled", "off", "false", "0", "no"];
      if (truthy.includes(s)) return true;
      if (falsy.includes(s)) return false;
      if (s.includes("enable")) return true;
      if (s.includes("not") || s.includes("disable")) return false;
    }
    return Boolean(val);
  }

  useEffect(() => {
    let mounted = true;
    async function loadSettings() {
      try {
       
         const res = await fetch(`${API_BASE_URL}/api/Setting/All_Setting`, {
    method: "GET",
    headers: authHeaders(), // <-- include auth headers here
  });
        if (!res.ok) throw new Error("Network response was not ok");
        const data = await res.json();
        if (!mounted) return;
        if (Array.isArray(data) && data.length > 0) {
          const first = data[0];
          const boolVal = (typeof first.takesnapSort === 'boolean') ? first.takesnapSort : parseBool(first.takesnapSort ?? first.takesnapSort);
          const sid = first.Sid ?? first.sid ?? null;
          const count = first.SnapsortCount ?? first.snapsortCount ?? null;
          setSettingId(sid);
          setSnapOn(Boolean(boolVal));
          setSnapsPerHour(count !== null && count !== undefined ? String(count) : "");
          localStorage.setItem("snapOn", JSON.stringify(Boolean(boolVal)));
          if (count !== null && count !== undefined) localStorage.setItem("snapsPerHour", JSON.stringify(count));
        } else {
          const savedSnap = localStorage.getItem("snapOn");
          const savedCount = localStorage.getItem("snapsPerHour");
          if (savedSnap !== null) setSnapOn(JSON.parse(savedSnap));
          if (savedCount !== null) setSnapsPerHour(String(JSON.parse(savedCount)));
        }
      } catch (err) {
        const savedSnap = localStorage.getItem("snapOn");
        const savedCount = localStorage.getItem("snapsPerHour");
        if (savedSnap !== null) setSnapOn(JSON.parse(savedSnap));
        if (savedCount !== null) setSnapsPerHour(String(JSON.parse(savedCount)));
      }
    }

    loadSettings();
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    localStorage.setItem("snapOn", JSON.stringify(snapOn));
  }, [snapOn]);

  useEffect(() => {
    localStorage.setItem("snapsPerHour", JSON.stringify(snapsPerHour));
  }, [snapsPerHour]);

  const timerRef = useRef(null);
  function handleToggle(value) {
    const prev = snapOn;
    setSnapOn(value);
    setUnsaved(true);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      setStatusMsg("");
      setStatusType("");
    }, 3000);
  }

  async function saveSettings() {
    if (saving) return;
    if (settingId == null) {
      setStatusMsg("No setting id available to update on server.");
      setTimeout(() => setStatusMsg(""), 3000);
      return;
    }

    setSaving(true);
    try {
      const countVal = snapsPerHour === "" ? null : Number.parseInt(snapsPerHour, 10);
      const payload = { Sid: settingId, TakesnapSort: snapOn, SnapsortCount: Number.isNaN(countVal) ? null : countVal };
     const res = await fetch(`${API_BASE_URL}/api/Setting/Update_Setting`, {
  method: "PUT",
  headers: authHeaders(),
  body: JSON.stringify(payload),
});

      if (!res.ok) throw new Error("Update failed");
      const body = await res.json().catch(() => null);
      const msg = (body && (body.Message || body.message)) || "Settings saved.";
      setStatusMsg(msg);
      setStatusType("success");
      setUnsaved(false);
      localStorage.setItem("snapOn", JSON.stringify(snapOn));
      localStorage.setItem("snapsPerHour", JSON.stringify(snapsPerHour));
    } catch (err) {
      setStatusMsg("Failed to save settings.");
      setStatusType("error");
    } finally {
      setSaving(false);
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        setStatusMsg("");
        setStatusType("");
      }, 2000);
    }
  }

  return (
    <div>
      <div id="sidebar-overlay" />
      <Sidebar />
      <div id="content">
        <div className="d-flex align-items-center mb-3">
          <button type="button" id="sidebarCollapse" className="btn btn-toggle me-3" onClick={toggleSidebar}>
            <i className="fas fa-bars" />
          </button>
          <a href="/" className="text-muted text-decoration-none small"><i className="fas fa-chevron-left" /> Back</a>
        </div>
        <div className="product-page-wrapper">
          <div className="product-page container-fluid"></div>


          <div className="main-card">
            <div className="panel panel-default">
              <div className="panel-body p-0">
                <div className="mb-0 p-4">
                  {/* TOAST */}
                  <div className="d-flex justify-content-end mb-2">
                    <div className={`toast-container ${statusMsg ? "show" : ""}`}>
                      {statusMsg && (
                        <div className={`toast-message ${statusType || "info"}`}>
                          {statusMsg}
                          <button
                            className="toast-close"
                            onClick={() => {
                              setStatusMsg("");
                              setStatusType("");
                              if (timerRef.current) clearTimeout(timerRef.current);
                            }}
                            aria-label="Close"
                          >
                            ×
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                  <h4 className="mb-3">Settings</h4>
                  <div className="form-group">
                    <label className="setting-row">
                      <div className="setting-label">
                        <span className="title">Enable Snap</span>
                        <span className="desc">Allow quick snapshots for activity monitoring</span>
                      </div>

                      <div className="setting-control">
                        <label className="switch">
                          <input
                            id="snap-toggle"
                            className="switch-input"
                            type="checkbox"
                            checked={snapOn}
                            aria-checked={snapOn}
                            onChange={(e) => handleToggle(e.target.checked)}
                          />
                          <span className="switch-slider" />
                        </label>
                      </div>
                    </label>
                  </div>


                  <div className="form-group" style={{ marginTop: 5 }}>
                    <label className="setting-row">
                      <div className="setting-label">
                        How many Snaps in 1 hour?
                      </div>
                      <input
                        id="snapsPerHour"
                        type="number"
                        className="w-full border border-gray-300 rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Enter a number"
                        value={snapsPerHour}
                        onChange={(e) => { setSnapsPerHour(e.target.value); setUnsaved(true); }}
                      />
                    </label>
                  </div>

                </div>
              </div>
              <div className="d-flex  p-4 border-top">
                <button
                  className="btn btn-primary saveUnsaved"
                  onClick={saveSettings}
                  disabled={!unsaved || saving}
                >
                  {saving ? "Saving..." : "Save Changes"}
                </button>

              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Settings;
